import React, { useEffect, useState } from 'react';
import { Container, Typography, Card, CardContent, Button, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import CustomCircularProgress from '../../components/Loaders/CustomCircularProgress';
import { useNavigate } from 'react-router-dom';

const Pricing = (props) => {
  const navigate = useNavigate();
  const { isAuth, subscribed, checkSubscription, user } = useAuth();
  const { theme } = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      setIsLoading(true);
      await checkSubscription(user);
      setIsLoading(false);
    };
    checkAccess();
  }, []);

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.background.primary,
      minHeight: '100%'
    },
    card: {
      maxWidth: 350,
      backgroundColor: theme.background.secondary,
      margin: 'auto',
      textAlign: 'center',
      borderRadius: '15px',
      boxShadow: `0 4px 20px ${theme.color.secondary}`,
      '&:hover': {
        boxShadow: `0 4px 20px ${theme.accent.marter}`,
      },
    },
    typography: {
      color: theme.text.primary,
      fontFamily: theme.fontFamily,
      display: 'flex',
      alignItems: 'center',
      margin: '10px 10px 10px 20px',
    },
    roadmap: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '20px',
      margin: '0 auto',
      maxWidth: '1000px',
    },
    roadmapStep: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: theme.background.secondary,
      borderRadius: '8px',
      boxShadow: `0 4px 6px ${theme.color.secondary}`,
      padding: '15px',
      width: '200px',
      position: 'relative',
      height: '170px',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        right: '-20px',
        width: '20px',
        height: '4px',
        backgroundColor: theme.color.secondary,
        zIndex: 0,
      },
      '&:last-child::after': {
        display: 'none'
      },
      '&:hover': {
        boxShadow: `0 4px 6px ${theme.accent.marter}`,
      }
    },
    stepIcon: {
      width: '50px',
      height: '50px',
      background: '#9e9e9e',
      color: theme.text.negative,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '10px',
    },
    h3: {
      margin: '0 0 5px',
      fontSize: '16px',
      color: theme.text.primary,
      textAlign: 'center',
    },
    p: {
      margin: 0,
      fontSize: '14px',
      color: theme.text.secondary,
      textAlign: 'center',
    }
  }

  return (
    <Box sx={styles.container}>
      <Container maxWidth="md">
        <Box sx={{ my: 4, }}>
          <Typography variant="h2" component="h1" gutterBottom align="center" style={{ fontFamily: theme.fontFamily, color: theme.color.primary, }}>
            Pricing
          </Typography>
        </Box>
        <Grid container justifyContent="center">
          <Grid item style={{ display: "flex", flexDirection: "row" }}>
            {isLoading ? (
              <CustomCircularProgress />
            ) : (<>
              <Card sx={styles.card}>
                <CardContent>
                  <Typography variant="h5" component="div" gutterBottom style={{ color: theme.text.primary, fontFamily: theme.fontFamily, }}>
                    IF Monthly
                  </Typography>

                  {isYearly && <Typography style={{ fontFamily: theme.fontFamily, fontSize: 14, }}>* billed yearly</Typography>}
                  <Typography style={{ color: theme.color.primary, fontFamily: theme.fontFamily, fontSize: '2.5rem', fontWeight: 'bold', marginBottom: 5, }}>
                    <span style={{ fontSize: "50%", color: theme.accent.error, textDecoration: "line-through", marginRight: '10px' }}>$79</span>${isYearly ? 62 : 59}<Typography component="span" variant="h6">/month</Typography>
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> 7-Day Free Trial
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> Locked-In Price
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> Full Access to Inventory Falcon
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={subscribed}
                    onClick={() => {
                      if (isAuth) {
                        if (isYearly) {
                          window.location.href = `https://store.payproglobal.com/checkout?products%5B1%5D%5Bid%5D=108820&page-template=21043&language=en&currency=USD&billing-email=${user}`;
                        } else {
                          window.location.href = `https://store.payproglobal.com/checkout?products%5B1%5D%5Bid%5D=108820&page-template=21043&language=en&currency=USD&billing-email=${user}`;
                        }
                      } else {
                        navigate('/login');
                      }
                    }}
                    style={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.text.negative,
                      borderRadius: '20px',
                      backgroundColor: subscribed ? '#9e9e9e' : theme.color.primary,
                      border: `1px solid ${theme.border.dark}`,
                      marginTop: 15
                    }}
                  >
                    {subscribed ? 'Current Plan' : isAuth ? 'Get Started' : "Login"}
                  </Button>
                </CardContent>
              </Card>&emsp;&emsp;
              <Card sx={styles.card}>
                <CardContent>
                  <Typography variant="h5" component="div" gutterBottom style={{ color: theme.text.primary, fontFamily: theme.fontFamily, }}>
                    IF Annually
                  </Typography>

                  {isYearly && <Typography style={{ fontFamily: theme.fontFamily, fontSize: 14, }}>* billed yearly</Typography>}
                  <Typography style={{ color: theme.color.primary, fontFamily: theme.fontFamily, fontSize: '2.5rem', fontWeight: 'bold', marginBottom: 5, }}>
                    <span style={{ fontSize: "50%", color: theme.accent.error, textDecoration: "line-through", marginRight: '10px' }}>$790</span>${isYearly ? 62 : 590}<Typography component="span" variant="h6">/year</Typography>
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> 7-Day Free Trial
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> Locked-In Price
                  </Typography>
                  <Typography style={styles.typography}>
                    <CheckCircleIcon style={{ color: theme.color.primary, }} sx={{ mr: 1 }} /> Full Access to Inventory Falcon
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={subscribed}
                    onClick={() => {
                      if (isAuth) {
                        if (isYearly) {
                          window.location.href = `https://store.payproglobal.com/checkout?products%5B1%5D%5Bid%5D=108821&page-template=21043&language=en&currency=USD&billing-email=${user}`;
                        } else {
                          window.location.href = `https://store.payproglobal.com/checkout?products%5B1%5D%5Bid%5D=108821&page-template=21043&language=en&currency=USD&billing-email=${user}`;
                        }
                      } else {
                        navigate('/login');
                      }
                    }}
                    style={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.text.negative,
                      borderRadius: '20px',
                      backgroundColor: subscribed ? '#9e9e9e' : theme.color.primary,
                      border: `1px solid ${theme.border.dark}`,
                      marginTop: 15
                    }}
                  >
                    {subscribed ? 'Current Plan' : isAuth ? 'Get Started' : "Login"}
                  </Button>
                </CardContent>
              </Card>
            </>
            )}
            <img src="https://www.nepeto.com/assets/PIONEERS.png" onClick={() => window.open("https://www.getmarter.com/pioneers-dashboard")} style={{ width: "30%", borderRadius: "20px", boxShadow: "0px 0px 5px 0px green", cursor: "pointer", margin: "auto", marginLeft: "5%", display: "block" }} />
          </Grid>
        </Grid>
        <br /><br />
        <h1 style={{ color: theme.text.primary, textAlign: "center" }}>Roadmap</h1>
        <Box sx={styles.roadmap}>
          <Box sx={styles.roadmapStep}>
            <Box sx={{ ...styles.stepIcon, background: '#4caf50', }}>✓</Box> {/* Complete */}
            <Box>
              <h3 style={styles.h3}>Step 1: IF Core</h3>
              <p style={styles.p}>Add WFS/SF Items, WFS Shipments, Marter Integration, CSV Upload, and More.</p>
            </Box>
          </Box>
          <Box sx={styles.roadmapStep}>
            <Box sx={{ ...styles.stepIcon, background: '#4caf50', }}>✓</Box> {/* Complete */}
            <Box>
              <h3 style={styles.h3}>Step 2: Reports & Analytics</h3>
              <p style={styles.p}>Adding Reports & Analytics for Everything You Need!</p>
            </Box>
          </Box>
          <Box sx={styles.roadmapStep}>
            <Box sx={styles.stepIcon}>3</Box>
            <Box>
              <h3 style={styles.h3}>Step 3: Tracking</h3>
              <p style={styles.p}>Inventory tracking, stock, orders and on-demand notifications!</p>
            </Box>
          </Box>
          <Box sx={styles.roadmapStep}>
            <Box sx={styles.stepIcon}>4</Box>
            <Box>
              <h3 style={styles.h3}>Step 4: TBD</h3>
              <p style={styles.p}>It will be worth the wait.</p>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" style={{ color: theme.text.secondary, fontFamily: theme.fontFamily, }}>
            Have questions? Contact our support team for more information.<br />
            Email: team@inventoryfalcon.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;